import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { combineLatest, Observable, of } from 'rxjs';
import { distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import { TcsCustomPermission } from 'src/app/shared/authorization/permissions';
import { AppState } from 'src/store/appstate';
import { selectCompanyByCompanyCode } from 'src/store/entityStores/company-selectors';
import { ActivatedRouteSnapshot } from '@angular/router';
import {
  CommunicationCenterProps,
  selectAllCommunicationCenterEntities,
} from 'src/store/generated/communication-center-record.generated';
import { UserViewModel } from 'src/models/person-view-model';
import { AccessibleItemProps } from 'src/store/generated/accessible-item-record.generated';
import { EntityType, UserRole } from 'src/graphql/generated/graphql.generated';
import { Seq, Set } from 'immutable';
import { undefinedOrSwitchMap } from 'src/util/rxjs';
import { CompanyContextService } from 'src/app/shared/company-context.service';
import { filterNull } from 'src/util/rxjs/operators/filter-null';
import { PermissionsService } from '../amg-authorization/permissions.service';
import { UserService } from '../tcs-identity/user.service';
import { AccessibleItemAuthorizationService } from './accessible-item-authorization.service';

@Injectable({ providedIn: 'root' })
export class OosAuthorizationService {
  constructor(
    private _accessibleItems: AccessibleItemAuthorizationService,
    private _user: UserService,
    private _permissions: PermissionsService,
    private _store: Store<AppState>,
    private _companyContextService: CompanyContextService,
  ) {}

  public isAuthorizedForRoute(
    route: ActivatedRouteSnapshot,
    companyCode: string,
  ): Observable<boolean> {
    return this.isAuthorizedForCompanyCode(companyCode).pipe(
      switchMap((isAuthorized) => {
        if (!isAuthorized) {
          return of(false);
        }
        return of(true);
      }),
    );
  }

  public isAuthorizedForCompanyCode(companyCode: string) {
    return this._store
      .select(selectCompanyByCompanyCode(companyCode))
      .pipe(switchMap((c) => (c != null ? this.isAuthorizedForCompanyId(c.id) : of(false))));
  }

  public isAuthorizedForCompanyId(companyId: string) {
    return combineLatest([this._isAuthorizedForCompany(companyId)]).pipe(
      map((grants) => {
        const anyGranted = grants.find((hasPermission) => hasPermission) != null;
        return anyGranted;
      }),
      distinctUntilChanged(),
    );
  }

  public isAuthorizedForCommCenter(commCenterId: string): Observable<boolean> {
    return this.getAuthorizedCommCenters(undefined, undefined).pipe(
      map((_) => _.find((cc) => cc.id === commCenterId) != null),
    );
  }

  public isAuthorizedToManageCommCenter(commCenterId: string): Observable<boolean> {
    return this.getAuthorizedCommCenters(undefined, TcsCustomPermission.ManageOosRum).pipe(
      map((_) => _.find((cc) => cc.id === commCenterId) != null),
    );
  }

  public getAuthorizedCommCentersForCurrentCompany(
    permission?: TcsCustomPermission,
  ): Observable<Seq.Indexed<CommunicationCenterProps>> {
    return this._companyContextService.company$.pipe(
      filterNull(),
      switchMap((company) => this.getAuthorizedCommCenters(company.id, permission)),
    );
  }

  public getAuthorizedCommCenters(
    companyId?: string,
    permission?: TcsCustomPermission,
  ): Observable<Seq.Indexed<CommunicationCenterProps>> {
    return this._user.user$.pipe(
      undefinedOrSwitchMap((user) => {
        const commCenters$ = this._store
          .select(selectAllCommunicationCenterEntities)
          .pipe(
            map((commCenters) =>
              commCenters.filter((c) => companyId == null || companyId === c.companyId).valueSeq(),
            ),
          );
        const accessibleItems$ = this._accessibleItems.getAccessibleItems();
        return combineLatest([commCenters$, accessibleItems$]).pipe(
          map(([commCenters, accessibleItems]) => {
            if (
              this._checkUserAllAccessiblePermissions(user, accessibleItems, companyId, permission)
            ) {
              return commCenters;
            }
            return commCenters.filter((cc) => {
              return this._checkAuthorizedForCompany(
                cc.companyId,
                user,
                accessibleItems,
                cc.id,
                permission,
              );
            });
          }),
        );
      }),
      map((ccs) => ccs ?? Seq.Indexed<CommunicationCenterProps>()),
    );
  }

  private _isAuthorizedForCompany(companyId: string, commCenterId?: string) {
    const commCenters$ = this._store
      .select(selectAllCommunicationCenterEntities)
      .pipe(
        map((commCenters) =>
          commCenters.filter((c) => companyId == null || companyId === c.companyId).valueSeq(),
        ),
      );

    const accessibleItems$ = this._accessibleItems.getAccessibleItems();
    return combineLatest([this._user.user$, accessibleItems$, commCenters$]).pipe(
      map(([user, accessibleItems, commCenters]) => {
        if (commCenters.count() > 0) {
          const authorizedForCompany = commCenters.filter((cc) => {
            return this._checkAuthorizedForCompany(cc.companyId, user, accessibleItems, cc.id);
          });
          return authorizedForCompany.count() > 0;
        } else {
          return this._checkAuthorizedForCompany(companyId, user, accessibleItems, commCenterId);
        }
      }),
    );
  }

  private _checkAuthorizedForCompany(
    companyId: string,
    user: UserViewModel | undefined,
    accessibleItems: AccessibleItemProps[],
    commCenterId?: string,
    permission?: TcsCustomPermission,
  ) {
    if (user == null) {
      return false;
    }

    if (this._checkUserAllAccessiblePermissions(user, accessibleItems, companyId, permission)) {
      return true;
    }

    const commCenterAi = accessibleItems.find(
      (ai) => ai.entityId === commCenterId && ai.type === EntityType.CommunicationCenter,
    );
    if (this._hasScopedPermission(commCenterAi, permission)) {
      // User has been granted access to this specific comm center
      return true;
    }
  }

  private _checkUserAllAccessiblePermissions(
    user: UserViewModel,
    accessibleItems: AccessibleItemProps[],
    companyId?: string,
    permission?: TcsCustomPermission,
  ) {
    if (this._hasScopedPermission(user, permission)) {
      return true;
    }
    if (companyId != null) {
      const companyAi = accessibleItems.find(
        (ai) => ai.entityId === companyId && ai.type === EntityType.Company,
      );
      if (companyAi?.userRoles != null && this._hasScopedPermission(companyAi, permission)) {
        // User has been granted access to this comm center because
        // they have accesss to all comm centers in this company
        return true;
      }
    }
    return false;
  }

  private _hasScopedPermission(
    scope:
      | {
          userRoles: Set<UserRole> | null;
        }
      | undefined,
    permission?: TcsCustomPermission,
  ) {
    const permissionToCheck = permission ?? TcsCustomPermission.AccessOosRum;
    if (scope == null) {
      return false;
    }
    const { userRoles } = scope;
    if (userRoles == null) {
      return false;
    }
    return this._permissions.hasPermission(userRoles.toArray(), permissionToCheck);
  }
}
